import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { RolesService } from '@/apiConnection/roles.service';
import { Utils } from '@/utils/utils';
import { LoadingComponent } from '@/components/loading/loading.component';

interface PanelRow {
  panel_id: number;
  label: string;
  name: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
  allowed_document_types: string[] | null;
}

@Component({
  selector: 'app-role-panels',
  imports: [
    CustomModalComponent,
    CommonModule,
    FormsModule,
    LoadingComponent,
  ],
  templateUrl: './role-panels.component.html',
})
export class RolePanelsComponent implements OnInit {
  @Input() roleId = 0;
  @Input() roleName = '';
  @Output() closeFunction = new EventEmitter<void>();
  @Output() acceptFunction = new EventEmitter<void>();

  loading = signal(true);
  saving = signal(false);
  rows: PanelRow[] = [];

  readonly documentTypes = [
    { value: 'factura',     label: 'Factura' },
    { value: 'albaran',     label: 'Albarán' },
    { value: 'devolucion',  label: 'Devolución' },
    { value: 'presupuesto', label: 'Presupuesto' },
    { value: 'proforma',    label: 'Proforma' },
  ];

  constructor(private rolesService: RolesService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.loadData();
  }

  private loadData() {
    this.loading.set(true);
    this.rolesService.getAllPanels().subscribe({
      next: (panelsRes: any) => {
        const allPanels: any[] = panelsRes.panels ?? [];

        this.rolesService.getRolePanels(this.roleId).subscribe({
          next: (roleRes: any) => {
            const assigned: any[] = roleRes.panels ?? [];
            this.rows = allPanels.map((p) => {
              const existing = assigned.find((a: any) => a.id === p.id);
              let allowedDocTypes: string[] | null = null;
              if (p.name === 'documents' && existing?.pivot?.allowed_document_types) {
                const raw = existing.pivot.allowed_document_types;
                allowedDocTypes = Array.isArray(raw) ? raw : JSON.parse(raw);
              }
              return {
                panel_id:               p.id,
                label:                  p.label,
                name:                   p.name,
                can_read:               existing?.pivot?.can_read   ?? false,
                can_create:             existing?.pivot?.can_create ?? false,
                can_update:             existing?.pivot?.can_update ?? false,
                can_delete:             existing?.pivot?.can_delete ?? false,
                allowed_document_types: allowedDocTypes,
              };
            });
            this.loading.set(false);
            this.cdr.detectChanges();
          },
          error: () => (this.loading.set(false)),
        });
      },
      error: () => (this.loading.set(false)),
    });
  }

  get allRead(): boolean { return this.rows.length > 0 && this.rows.every(r => r.can_read); }
  get someRead(): boolean { return this.rows.some(r => r.can_read) && !this.allRead; }
  get hasAnyRead(): boolean { return this.rows.some(r => r.can_read); }

  get allCreate(): boolean {
    const readable = this.rows.filter(r => r.can_read);
    return readable.length > 0 && readable.every(r => r.can_create);
  }
  get someCreate(): boolean { return this.rows.some(r => r.can_create) && !this.allCreate; }

  get allUpdate(): boolean {
    const readable = this.rows.filter(r => r.can_read);
    return readable.length > 0 && readable.every(r => r.can_update);
  }
  get someUpdate(): boolean { return this.rows.some(r => r.can_update) && !this.allUpdate; }

  get allDelete(): boolean {
    const readable = this.rows.filter(r => r.can_read);
    return readable.length > 0 && readable.every(r => r.can_delete);
  }
  get someDelete(): boolean { return this.rows.some(r => r.can_delete) && !this.allDelete; }

  toggleAllRead(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.forEach(r => {
      r.can_read = checked;
      if (!checked) this.onReadChange(r);
    });
  }

  toggleAllCreate(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.filter(r => r.can_read).forEach(r => r.can_create = checked);
  }

  toggleAllUpdate(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.filter(r => r.can_read).forEach(r => r.can_update = checked);
  }

  toggleAllDelete(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.filter(r => r.can_read).forEach(r => r.can_delete = checked);
  }

  onReadChange(row: PanelRow) {
    if (!row.can_read) {
      row.can_create = false;
      row.can_update = false;
      row.can_delete = false;
      if (row.name === 'documents') {
        row.allowed_document_types = null;
      }
    }
  }

  isDocTypeAllowed(row: PanelRow, type: string): boolean {
    return !row.allowed_document_types || row.allowed_document_types.includes(type);
  }

  toggleDocType(row: PanelRow, type: string) {
    const allValues = this.documentTypes.map((t) => t.value);

    if (!row.allowed_document_types) {
      // Currently all allowed — uncheck this one, keep the rest
      row.allowed_document_types = allValues.filter((v) => v !== type);
      return;
    }

    const idx = row.allowed_document_types.indexOf(type);
    if (idx >= 0) {
      row.allowed_document_types.splice(idx, 1);
    } else {
      row.allowed_document_types.push(type);
    }

    // All selected → remove restriction
    if (row.allowed_document_types.length === allValues.length) {
      row.allowed_document_types = null;
    }
  }

  save() {
    this.saving.set(true);
    const panels = this.rows
      .filter((r) => r.can_read || r.can_create || r.can_update || r.can_delete)
      .map((r) => ({
        panel_id:   r.panel_id,
        can_read:   r.can_read,
        can_create: r.can_create,
        can_update: r.can_update,
        can_delete: r.can_delete,
        ...(r.name === 'documents'
          ? { allowed_document_types: r.allowed_document_types?.length ? r.allowed_document_types : null }
          : {}),
      }));

    this.rolesService.syncRolePanels(this.roleId, panels).subscribe({
      next: () => {
        Utils.notification.success('Permisos guardados');
        this.acceptFunction.emit();
        this.closeFunction.emit();
      },
      error: (error: any) => {
        this.saving.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar los permisos');
      },
      complete: () => (this.saving.set(false)),
    });
  }
}
